import { useState } from 'react';
import { Button } from './Button';
import styles from './PasswordDialog.module.css';

export type PasswordDialogMode =
  | 'card_remove'
  | 'password_add'
  | 'password_change'
  | 'password_remove'
  | 'password_required';

interface PasswordDialogProps {
  visible: boolean;
  mode: PasswordDialogMode;
  onSubmit: (passwords: { currentPassword?: string; newPassword?: string }) => void;
  onCancel: () => void;
  error?: string;
}

const MODE_CONFIG: Record<PasswordDialogMode, {
  title: string;
  showCurrent: boolean;
  showNew: boolean;
  showConfirm: boolean;
  submitText: string;
}> = {
  card_remove: {
    title: 'Enter Password',
    showCurrent: true,
    showNew: false,
    showConfirm: false,
    submitText: 'Submit',
  },
  password_add: {
    title: 'Add Password',
    showCurrent: false,
    showNew: true,
    showConfirm: true,
    submitText: 'Add Password',
  },
  password_change: {
    title: 'Change Password',
    showCurrent: true,
    showNew: true,
    showConfirm: true,
    submitText: 'Change Password',
  },
  password_remove: {
    title: 'Remove Password',
    showCurrent: true,
    showNew: false,
    showConfirm: false,
    submitText: 'Remove Password',
  },
  password_required: {
    title: 'Password Required',
    showCurrent: true,
    showNew: false,
    showConfirm: false,
    submitText: 'Submit',
  },
};

export function PasswordDialog({ visible, mode, onSubmit, onCancel, error }: PasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  if (!visible) return null;

  const config = MODE_CONFIG[mode];

  const handleSubmit = () => {
    setValidationError('');

    if (config.showNew) {
      if (newPassword.length < 4) {
        setValidationError('Password must be at least 4 characters.');
        return;
      }
      if (config.showConfirm && newPassword !== confirmPassword) {
        setValidationError('Passwords do not match.');
        return;
      }
    }

    if (config.showCurrent && !config.showNew && !currentPassword) {
      setValidationError('Please enter your password.');
      return;
    }

    onSubmit({
      currentPassword: config.showCurrent ? currentPassword : undefined,
      newPassword: config.showNew ? newPassword : undefined,
    });
  };

  const displayError = error || validationError;

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <h3 className={styles.title}>{config.title}</h3>

        {config.showCurrent && (
          <input
            type="password"
            className={styles.input}
            placeholder={config.showNew ? 'Current Password' : 'Password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoFocus
          />
        )}

        {config.showNew && (
          <>
            <input
              type="password"
              className={styles.input}
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus={!config.showCurrent}
            />
            {config.showConfirm && (
              <input
                type="password"
                className={styles.input}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            )}
          </>
        )}

        {displayError && <p className={styles.error}>{displayError}</p>}

        <div className={styles.buttonRow}>
          <Button theme="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSubmit}>{config.submitText}</Button>
        </div>
      </div>
    </div>
  );
}

export default PasswordDialog;
